import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close, Delete, Edit } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';

import { useGetCurrentUser } from '@/api/auth/auth';
import {
  classifyFlowgateResultError,
  useCreateFlowgateSelectionComment,
  useDeleteFlowgateSelectionComment,
  useFlowgateSelection,
  useFlowgateSelectionComments,
  useUpdateFlowgateSelectionComment,
  type FlowgateCommentType,
  type FlowgateSelectionComment,
  type FlowgateValidationStatus,
} from '@/api/flowgate';

const STATUS_LABEL: Record<FlowgateValidationStatus, string> = {
  pending: 'Pending',
  first_validated: '1st validated',
  second_validated: '2nd validated',
  rejected: 'Rejected',
};

const STATUS_COLOR: Record<
  FlowgateValidationStatus,
  'default' | 'info' | 'success' | 'error'
> = {
  pending: 'default',
  first_validated: 'info',
  second_validated: 'success',
  rejected: 'error',
};

const COMMENT_TYPE_LABEL: Record<FlowgateCommentType, string> = {
  general: 'General',
  first_validation: 'First validation',
  second_validation: 'Second validation',
  rejection: 'Rejection',
};

const sameEmail = (left?: string | null, right?: string | null) =>
  !!left && !!right && left.toLowerCase() === right.toLowerCase();

type Props = {
  selectionId: string;
  resultId?: string;
  variantLabel?: string;
  onClose: () => void;
};

const FlowgateSelectionDrawer = ({
  selectionId,
  resultId,
  variantLabel = '',
  onClose,
}: Props) => {
  const { data: currentUser } = useGetCurrentUser();
  const { data: selection } = useFlowgateSelection(selectionId);
  const { data: comments, isLoading: commentsLoading } =
    useFlowgateSelectionComments(selectionId);
  const createComment = useCreateFlowgateSelectionComment();
  const updateComment = useUpdateFlowgateSelectionComment();
  const deleteComment = useDeleteFlowgateSelectionComment();

  const [commentText, setCommentText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const surfaceError = (err: unknown) => {
    const info = classifyFlowgateResultError(err);
    enqueueSnackbar(info.message, {
      variant: info.kind === 'not_ready' ? 'warning' : 'error',
    });
  };

  const handleSend = () => {
    const text = commentText.trim();
    if (!text) return;
    createComment.mutate(
      {
        selectionId,
        resultId,
        body: {
          comment_type: 'general',
          comment: text,
          variant_label: variantLabel,
        },
      },
      {
        onSuccess: () => setCommentText(''),
        onError: surfaceError,
      },
    );
  };

  const startEdit = (comment: FlowgateSelectionComment) => {
    setEditingId(comment.id);
    setEditText(comment.comment);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = () => {
    const text = editText.trim();
    if (!editingId || !text) return;
    updateComment.mutate(
      {
        selectionId,
        commentId: editingId,
        resultId,
        body: { comment: text },
      },
      {
        onSuccess: () => cancelEdit(),
        onError: surfaceError,
      },
    );
  };

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          bgcolor: 'background.paper',
        },
      }}
    >
      <Stack
        sx={{ height: '100%', overflowY: 'auto', p: { xs: 2, sm: 2.5 } }}
        spacing={2}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Box>
            <Typography variant="h3">Variant review</Typography>
            <Typography variant="body2" color="text.secondary">
              Row {selection?.row_id ?? '—'}
            </Typography>
            {selection?.created_by_email && (
              <Typography variant="caption" color="text.secondary">
                Selected by {selection.created_by_email}
              </Typography>
            )}
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            {selection && (
              <Chip
                size="small"
                label={STATUS_LABEL[selection.validation_status]}
                color={STATUS_COLOR[selection.validation_status]}
              />
            )}
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
        </Stack>

        <Box>
          <Typography variant="subtitle1">Comments</Typography>
          <Typography variant="body2" color="text.secondary">
            Document review context and validation decisions.
          </Typography>
        </Box>
        {commentsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!commentsLoading && (!comments || comments.length === 0) && (
          <Typography variant="body2" color="text.secondary">
            No comments yet.
          </Typography>
        )}
        {!commentsLoading && comments && comments.length > 0 && (
          <List dense disablePadding>
            {comments.map((c) => {
              const mine = sameEmail(c.created_by_email, currentUser?.email);
              const isRejection = c.comment_type === 'rejection';
              const editing = editingId === c.id;
              const showActions = (mine || !isRejection) && !editing;
              return (
                <ListItem
                  key={c.id}
                  alignItems="flex-start"
                  sx={{
                    mb: 1,
                    py: 1,
                    pl: 1.5,
                    pr: showActions ? 12 : 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                  }}
                  secondaryAction={
                    showActions ? (
                      <Stack direction="row" spacing={0.25}>
                        {mine && (
                          <IconButton
                            size="small"
                            aria-label="Edit comment"
                            onClick={() => startEdit(c)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        {!isRejection && (
                          <IconButton
                            size="small"
                            aria-label="Delete comment"
                            disabled={deleteComment.isPending}
                            onClick={() =>
                              deleteComment.mutate(
                                {
                                  selectionId,
                                  commentId: c.id,
                                  resultId,
                                },
                                { onError: surfaceError },
                              )
                            }
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    ) : undefined
                  }
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Chip
                          size="small"
                          label={COMMENT_TYPE_LABEL[c.comment_type]}
                          variant={isRejection ? 'filled' : 'outlined'}
                          color={isRejection ? 'error' : 'default'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {c.created_by_email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(c.created_at).toLocaleString()}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      editing ? (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <TextField
                            autoFocus
                            multiline
                            minRows={2}
                            size="small"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            inputProps={{ 'aria-label': 'Edit comment' }}
                          />
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={saveEdit}
                              disabled={
                                !editText.trim() ||
                                editText.trim() === c.comment.trim() ||
                                updateComment.isPending
                              }
                            >
                              Save
                            </Button>
                            <Button
                              size="small"
                              onClick={cancelEdit}
                              disabled={updateComment.isPending}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                        >
                          {c.comment}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        <Divider />

        <Stack
          spacing={1}
          sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1.5 }}
        >
          <Typography variant="subtitle2">Add review comment</Typography>
          <TextField
            multiline
            minRows={3}
            placeholder="Write a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!commentText.trim() || createComment.isPending}
          >
            Send
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default FlowgateSelectionDrawer;
